import { ReactNode, useCallback, useMemo } from 'react';
import { QUOTE_API } from '~/lib/constants';
import useQuote from '~/hooks/use-quote';
import useBasket from '~/hooks/use-basket';
import { BasketSections } from '~/lib/types/enums';
import { BasketQuote, QuoteAddress, QuoteResponse } from '~/lib/types/basket-types';
import { authenticatedFetchWithTenantAnalytics } from '~/lib/fetch';
import { useAuth } from 'react-oidc-context';
import { useTenant, useAnalytics } from '@parcel2go/npm-p2g-app';
import useSWR from 'swr';
import useDropShopProviders from '~/hooks/use-drop-shop-providers';
import { P2GGrid } from '@parcel2go/core-ui';
import ServiceUpgradeOption from '~/components/core/switcher/service-upgrade-option';
import { findClosestService, findFasterService, isQuoteLetterBoxService } from '~/lib/quotes';
import { capturePromiseException } from '~/lib/metrics';

export default function ServiceUpgrade({ wrapper = (x) => x }: { wrapper?: (x: ReactNode) => ReactNode }) {
  const { quote } = useQuote();
  const { item, currentItemId, id, changeService } = useBasket();
  const { user } = useAuth();
  const { tenant, settings } = useTenant();
  const { session, track } = useAnalytics();

  const o = useMemo(() => {
    const origin = item.sections[BasketSections.ORIGIN]?.payload as QuoteAddress;
    return { postcode: origin?.postcode, isoCode: origin?.country };
  }, [item]);

  const shops = useDropShopProviders({ postcode: o.postcode, isoCode: o.isoCode });

  const { data, isLoading } = useSWR<QuoteResponse>(
    `${QUOTE_API}/quote/basket/${id}?itemIds=${currentItemId}&service=`,
    (url: string) =>
      authenticatedFetchWithTenantAnalytics(user, tenant, settings, session, url)
        .then((res) => res.json())
        .catch(capturePromiseException),
    { revalidateOnFocus: false, revalidateOnReconnect: false, revalidateIfStale: true, revalidateOnMount: true }
  );

  const filteredData = useMemo(() => {
    if (isLoading || !data?.result?.length || !quote) return undefined;
    // Hide letterbox services if the current quote isn't specifically a letter service
    // This is because parcel services can take letters, but not the other way around.
    return isQuoteLetterBoxService(quote)
      ? data
      : { ...data, result: data.result?.filter((q) => !isQuoteLetterBoxService(q)) };
  }, [isLoading, data, quote]);

  const fastest: BasketQuote | undefined = useMemo(() => {
    if (isLoading || !filteredData) return undefined;
    const faster = findFasterService(filteredData.result, quote);
    if (!faster) return undefined;
    return { ...faster, currency: filteredData.currency };
  }, [isLoading, filteredData, quote]);

  const closest: BasketQuote | undefined = useMemo(() => {
    if (
      isLoading ||
      !filteredData ||
      shops.loading ||
      !shops.shopProviders ||
      Object.keys(shops.shopProviders).length === 0 ||
      !o.postcode
    )
      return undefined;
    const closer = findClosestService(filteredData.result, quote, shops.shopProviders);
    if (!closer) return undefined;
    return { ...closer, currency: filteredData.currency };
  }, [isLoading, filteredData, shops.loading, shops.shopProviders, o.postcode, quote]);

  const onChangeService = useCallback(
    async (newQuote: BasketQuote) => {
      await changeService(newQuote);
      track({
        type: 'service_upgrade_selected',
        payload: { quote: newQuote, priceDifference: newQuote.price.net - quote.price.net }
      });
    },
    [changeService, quote.price.net, track]
  );

  if (!closest && !fastest) return null;

  return wrapper(
    <>
      {fastest ? (
        <P2GGrid item dataTestId="service-upgrade-faster">
          <ServiceUpgradeOption newQuote={fastest} originalQuote={quote} onSelect={onChangeService} />
        </P2GGrid>
      ) : (
        closest && (
          <P2GGrid item dataTestId="service-upgrade-closer">
            <ServiceUpgradeOption
              newQuote={closest}
              originalQuote={quote}
              newShops={shops.shopProviders[closest.service.dropOffProviderGroup]}
              originalShops={shops.shopProviders[quote.service.dropOffProviderGroup]}
              onSelect={onChangeService}
            />
          </P2GGrid>
        )
      )}
    </>
  );
}
